import { cn } from "@gotts.ai/ui";
import { AlertTriangle, Check, Copy, Loader2 } from "lucide-react";
import { useState } from "react";

type TxState =
  | { status: "pending"; hash: string }
  | { status: "confirmed"; hash: string; blockNumber: number }
  | { status: "failed"; hash: string; reason: string };

interface TransactionStatusProps {
  txState: TxState;
}

export function TransactionStatus({ txState }: TransactionStatusProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(txState.hash);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };

  return (
    <div
      className={cn(
        "mx-3 mb-2 mt-1 border-l-2 px-2.5 py-2",
        txState.status === "pending" &&
          "border-l-[var(--color-gold)]/40 bg-[rgba(201,168,76,0.04)]",
        txState.status === "confirmed" &&
          "border-l-green-500/40 bg-[rgba(52,209,106,0.04)]",
        txState.status === "failed" &&
          "border-l-red-500/40 bg-[rgba(239,68,68,0.04)]",
      )}
    >
      <div className="flex items-center gap-1.5">
        {txState.status === "pending" && (
          <>
            <Loader2
              size={12}
              className="animate-spin text-[var(--color-gold)]"
            />
            <span className="text-[11px] text-[var(--color-gold)]">
              Transaction pending...
            </span>
          </>
        )}
        {txState.status === "confirmed" && (
          <>
            <Check size={12} className="text-[var(--color-success)]" />
            <span className="text-[11px] text-[var(--color-success)]">
              Confirmed in block #{txState.blockNumber.toLocaleString()}
            </span>
          </>
        )}
        {txState.status === "failed" && (
          <>
            <AlertTriangle size={12} className="text-[var(--color-error)]" />
            <span className="text-[11px] text-[var(--color-error)]">
              Failed: {txState.reason}
            </span>
          </>
        )}
      </div>

      <div className="mt-1 flex items-center gap-1">
        <span className="font-mono text-[9px] text-[var(--color-text-muted)]">
          {txState.hash}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text)]"
          title="Copy hash"
        >
          {copied ? (
            <Check size={10} className="text-[var(--color-success)]" />
          ) : (
            <Copy size={10} />
          )}
        </button>
      </div>
    </div>
  );
}
